import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Crosshair } from "lucide-react";

/**
 * Reusable Mapbox map for the Public Property Search split view.
 *
 * SINGLE-ADDRESS MODE: to avoid the lag of plotting hundreds of markers, the
 * map renders at most ONE pin — the property the user selected from the list —
 * dropped on its exact address and zoomed in. Pass `properties` as an array
 * with 0 or 1 items. When empty, the map shows a "select a property" hint.
 */

const DEFAULT_CENTER = { lng: 144.9631, lat: -37.8136 }; // Melbourne
const MAP_STYLE = "mapbox://styles/mapbox/streets-v12";

// Pin colour per listing kind — on-brand teal for sale, distinct hues for the rest.
export const KIND_COLORS = {
    buy: "#55C5D0", // For sale (brand teal)
    rent: "#F59E0B", // For rent (amber)
    leased: "#B45309", // Leased: a rental that has been let (dark amber)
    sold: "#6366F1", // Sold (indigo)
    off_market: "#8B5CF6", // Off market: for sale, not on public portals (violet)
    not_for_sale: "#9CA3AF", // Not for sale: a database record that isn't listed (grey)
    collings: "#475569", // Our own scraped data, not a live campaign (slate)
    unlisted: "#9CA3AF", // A property profile with no current campaign (grey)
};

export const KIND_LABELS = {
    buy: "For sale",
    rent: "For rent",
    leased: "Leased",
    sold: "Sold",
    off_market: "Off Market",
    not_for_sale: "Not For Sale",
    collings: "Collings Data",
    unlisted: "Not listed",
};

function toNum(v) {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

const PropertyMapView = ({ accessToken, properties, fitTrigger, onSelect, selectedId }) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const onSelectRef = useRef(onSelect);

    const [mapReady, setMapReady] = useState(false);
    const [mapError, setMapError] = useState(null);

    useEffect(() => {
        onSelectRef.current = onSelect;
    }, [onSelect]);

    // Initialise the map once per access token
    useEffect(() => {
        if (!containerRef.current || !accessToken) return undefined;

        mapboxgl.accessToken = accessToken;
        setMapError(null);

        const container = containerRef.current;
        const map = new mapboxgl.Map({
            container,
            style: MAP_STYLE,
            center: [DEFAULT_CENTER.lng, DEFAULT_CENTER.lat],
            zoom: 10,
        });
        mapRef.current = map;

        const resizeMap = () => {
            try {
                map.resize();
            } catch {
                /* ignore */
            }
        };
        const resizeObserver = new ResizeObserver(() => resizeMap());
        resizeObserver.observe(container);

        const onLoad = () => {
            resizeMap();
            setMapReady(true);
        };
        const onErr = (e) => {
            setMapError(
                e?.error?.message || e?.message || (typeof e === "string" ? e : null) || "Map failed to load tiles"
            );
        };

        map.on("load", onLoad);
        map.on("error", onErr);

        return () => {
            resizeObserver.disconnect();
            map.off("load", onLoad);
            map.off("error", onErr);
            map.remove();
            mapRef.current = null;
            setMapReady(false);
        };
    }, [accessToken]);

    // Fly to the single selected property's exact address whenever it changes.
    useEffect(() => {
        const map = mapRef.current;
        if (!mapReady || !map || properties.length === 0) return;
        const p = properties[0];
        map.flyTo({ center: [toNum(p.longitude), toNum(p.latitude)], zoom: 16.5, speed: 1.4 });
    }, [mapReady, properties, fitTrigger]);

    // Render / re-render markers
    useEffect(() => {
        const map = mapRef.current;
        if (!mapReady || !map) return;

        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];

        properties.forEach((p) => {
            const lng = toNum(p.longitude);
            const lat = toNum(p.latitude);
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

            const color = KIND_COLORS[p.kind] || KIND_COLORS.not_for_sale;
            const isSelected = selectedId != null && p.id === selectedId;

            const marker = new mapboxgl.Marker({ color, scale: isSelected ? 1.35 : 1.05 })
                .setLngLat([lng, lat])
                .addTo(map);

            const el = marker.getElement();
            if (el) {
                el.style.cursor = "pointer";
                el.style.zIndex = isSelected ? "5" : "3";
                el.addEventListener("click", (e) => {
                    e.stopPropagation();
                    onSelectRef.current?.(p);
                });
            }
            markersRef.current.push(marker);
        });
    }, [mapReady, properties, selectedId]);

    useEffect(
        () => () => {
            markersRef.current.forEach((m) => m.remove());
            markersRef.current = [];
        },
        []
    );

    const locateMe = () => {
        const map = mapRef.current;
        if (!map || !navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => map.easeTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 14 }),
            () => {},
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    const selected = properties[0] || null;

    return (
        <div className="absolute inset-0">
            <div ref={containerRef} className="absolute inset-0 h-full w-full bg-[#e8eef3]" />

            {mapError ? (
                <div className="absolute inset-0 z-[2] flex items-center justify-center bg-[#EEF2F6]/95 p-4 text-center text-[13px] text-[#B91C1C]">
                    Map error: {mapError}
                </div>
            ) : null}

            {/* Selected-address banner (or hint when nothing is selected) */}
            <div className="pointer-events-none absolute left-1/2 top-3 z-[1] max-w-[92vw] -translate-x-1/2">
                <div className="flex items-center gap-2 rounded-2xl border border-gray-100 bg-white px-4 py-2 text-center shadow-md">
                    {selected ? (
                        <>
                            <span
                                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: KIND_COLORS[selected.kind] || KIND_COLORS.not_for_sale }}
                            />
                            <span className="text-[13px] font-semibold leading-tight text-[#36423F]">
                                {selected.address || selected.city || "Selected property"}
                            </span>
                        </>
                    ) : (
                        <span className="text-[13px] font-medium leading-tight text-gray-500">
                            Click a property to pinpoint it on the map
                        </span>
                    )}
                </div>
            </div>

            {/* Locate-me */}
            <button
                type="button"
                onClick={locateMe}
                disabled={!mapReady}
                className="absolute right-3 top-3 z-[1] flex h-10 w-10 items-center justify-center rounded-lg border border-gray-100 bg-white text-[#36423F] shadow-md hover:bg-gray-50 disabled:opacity-40"
                title="Centre on your location"
                aria-label="Centre on your location"
            >
                <Crosshair size={18} />
            </button>
        </div>
    );
};

export default PropertyMapView;
